import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Checkbox, CircularProgress, FormControlLabel, Stack, Typography } from '@mui/material';
import { API_BASE_URL, fetchJson } from './api';

export default function GmailIntegrationSettings({ navigate = (url) => window.location.assign(url) }) {
    const [connection, setConnection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [consent, setConsent] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const load = useCallback(async () => {
        setLoading(true);
        try { setConnection(await fetchJson(`${API_BASE_URL}/integrations/gmail`)); }
        catch (failure) { setError(failure.message || 'Gmail connection status could not be loaded.'); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => {
        const url = new URL(window.location.href);
        const outcome = url.searchParams.get('gmail');
        if (outcome) {
            if (outcome === 'connected') setMessage('Gmail connected. You can now search email from a project reporting period.');
            else setError(outcome === 'cancelled' ? 'Gmail linking was cancelled.' : 'Gmail linking failed or expired. Please try connecting again.');
            url.searchParams.delete('gmail');
            window.history.replaceState(window.history.state, '', url.toString());
        }
        load();
    }, [load]);
    async function connect() {
        setBusy(true); setError(''); setMessage('');
        try {
            const result = await fetchJson(connection.connect_url, {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ consent })
            });
            navigate(result.authorization_url);
        } catch (failure) { setError(failure.message || 'Gmail linking could not be started.'); }
        finally { setBusy(false); }
    }
    async function disconnect() {
        setBusy(true); setError(''); setMessage('');
        try {
            await fetchJson(`${API_BASE_URL}/integrations/gmail`, { method: 'DELETE' });
            setMessage('Gmail disconnected. New inbox searches are disabled. Previously captured project proposals are retained.');
        } catch (failure) { setError(failure.message || 'Gmail disconnection could not be confirmed.'); }
        finally { await load(); setBusy(false); setConsent(false); }
    }
    return <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
        <Typography variant="body2">Connect your own Gmail inbox with read-only access. Monsurv cannot send, edit or delete your emails.</Typography>
        <Typography variant="body2" color="text.secondary">Searches run only when you request them for an Initial or Interim Report. Each search requires consent to send matching email text to the configured AI provider and retain reviewable proposals in that project. Attachments are not processed.</Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {message ? <Alert severity="success">{message}</Alert> : null}
        {loading ? <CircularProgress size={22} aria-label="Loading Gmail connection" /> : null}
        {!loading && connection ? <>
            {!connection.configured ? <Alert severity="warning">Your administrator must configure Google OAuth and secure token storage before Gmail can be linked.</Alert> : null}
            <Typography variant="body2">{connection.connected ? `Connected inbox: ${connection.email_address}` : 'No Gmail inbox connected.'}</Typography>
            <FormControlLabel
                control={<Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)} disabled={busy || !connection.configured} />}
                label="I agree to link this Gmail account to Monsurv with read-only access."
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button variant="contained" onClick={connect} disabled={busy || !connection.configured || !consent}>
                    {connection.connected ? 'Reconnect Gmail' : 'Connect Gmail'}
                </Button>
                {connection.connected ? <Button variant="outlined" onClick={disconnect} disabled={busy}>Disconnect Gmail</Button> : null}
                <Button variant="text" onClick={() => { setError(''); load(); }} disabled={busy || loading}>Refresh status</Button>
            </Stack>
        </> : null}
    </Stack>;
}
